import { useState, useEffect, useRef } from 'react';
import Loading from 'components/Basic/Loading';
import ContentHeader from 'containers/Dashboard/ContentHeader';
import ShippingCostFilter from 'components/Dashboard/ShippingCost/ShippingCostFilter';
import ShippingCostTable from 'components/Dashboard/ShippingCost/ShippingCostTable';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import {
  useBulkIsAvailableMutation,
  useDeleteShippingCostMutation,
  useLazyGetShippingCostListQuery,
  usePatchIsAvailableMutation,
} from 'core/shippingCost/ShippingCostService';
import { setShippingCostList, setAvailable } from 'core/shippingCost/ShippingCostSlice';

import toast from 'components/Basic/Toast';
import { useIntl } from 'react-intl';

const ENTER_KEY = 13;
const BACKSPACE_KEY = 8;

const ShippingCostList = () => {
  const [state, setState] = useState({
    searchText: '',
    offset: 0,
    limit: 10,
    filterQuery: '',
    searchFilterQuery: '',
    searchItemText: '',
    searchRestaurantText: '',
    searchItemFilterQuery: '',
    searchRestaurantFilterQuery: '',
    isLoading: false,
    isCreateShippingCost: true,
    shippingCost: {},
    editShippingCost: {},
  });
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const [getShippingCostList] = useLazyGetShippingCostListQuery();
  const [deleteShippingCost] = useDeleteShippingCostMutation();
  const [patchIsAvailable] = usePatchIsAvailableMutation();
  const [bulkIsAvailable] = useBulkIsAvailableMutation();

  const { list } = useAppSelector(state => state.shippingCostReducer);

  const { available } = useAppSelector(state => state.shippingCostReducer);

  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    getInitialData();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleChangePage = (offset, limit, filterQuery) => {
    setState(prev => ({ ...prev, offset: offset, limit: limit, isLoading: true }));
    getShippingCostList({
      offset,
      limit,
      filter: filterQuery || state.filterQuery + state.searchFilterQuery,
      sort: 'id asc',
    })
      .unwrap()
      .then(res => {
        if (res?.shippingCosts.length === 0) {
          toast.error({
            title: intl.formatMessage({
              id: 'Shipping cost not found',
            }),
          });
          setState(prev => ({ ...prev, isLoading: false }));
          return;
        }

        dispatch(
          setShippingCostList({
            list: res,
          }),
        );
        if (!isMounted.current) return;
        setState(prev => ({ ...prev, isLoading: false }));
      })
      .catch(() => {
        if (!isMounted.current) return;
        setState(prev => ({ ...prev, isLoading: false }));
      });
  };

  const getInitialData = async () => {
    try {
      const res = await getShippingCostList({
        offset: state.offset,
        limit: state.limit,
        sort: 'id asc',
      }).unwrap();
      dispatch(setShippingCostList({ list: res }));
    } catch (e) {
      console.log(e);
    }
  };

  const handleSearch = e => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, '');
    setState(prev => ({ ...prev, searchText: onlyNums }));
  };

  const handleKeyDownSearch = async e => {
    const { keyCode, target } = e;

    const isEnter = keyCode === ENTER_KEY;
    const isBackspaceClear = keyCode === BACKSPACE_KEY && target.value.length === 1;

    if (!isEnter && !isBackspaceClear) return;

    let searchFilterQuery = state.searchFilterQuery || '';
    const params = {
      limit: state.limit,
      sort: 'id asc',
    };

    if (isEnter && state.searchText && !isNaN(state.searchText)) {
      searchFilterQuery = `&filter[id]=${state.searchText}`;
      params.filter = state.filterQuery + searchFilterQuery;
      params.offset = 0;
    }

    if (isBackspaceClear) {
      searchFilterQuery = '';
      params.filter = state.filterQuery;
      params.offset = state.filterQuery === '' && searchFilterQuery === '' ? state.offset : 0;
    }

    setState(prev => ({ ...prev, searchFilterQuery, isLoading: true }));

    try {
      const res = await getShippingCostList(params).unwrap();
      if (!res?.shippingCosts?.length) {
        toast.error({
          title: intl.formatMessage({ id: 'Shipping cost not found' }),
        });
        return;
      }
      dispatch(setShippingCostList({ list: res }));
    } catch (err) {
      console.error('Error fetching shipping cost list:', err);
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleIsAvailable = async (value, id) => {
    try {
      await patchIsAvailable({ id: id, isAvailable: value }).unwrap();
      toast.success({
        title: intl.formatMessage({
          id: 'Shipping cost is updated successfully',
        }),
      });
      handleApplyFilter();
    } catch (e) {
      toast.error({
        title: intl.formatMessage({
          id: 'Updating shipping cost is failure',
        }),
      });
    }
  };

  const handleSearchDayPart = e => {
    let searchFilterQuery = '';
    let dayPart = e.split(',');
    if (e === '') {
      searchFilterQuery = '';
    } else {
      searchFilterQuery += `&filter[openTime]=${dayPart[0]}&filter[closeTime]=${dayPart[1]}`;
    }
    setState(prev => ({ ...prev, filterQuery: searchFilterQuery }));
  };

  const handleApplyFilter = () => {
    setState(prev => ({ ...prev, isLoading: true }));

    const hasFilter = state.filterQuery !== '';
    const hasSearch = state.searchFilterQuery !== '';

    let params = {
      limit: state.limit,
      sort: 'id asc',
      ...(hasFilter || hasSearch ? { offset: 0 } : { offset: state.offset }),
      ...(hasFilter && !hasSearch && { filter: state.filterQuery }),
      ...(hasSearch && !hasFilter && { filter: state.searchFilterQuery }),
      ...(hasFilter &&
        hasSearch && {
          filter: state.filterQuery + state.searchFilterQuery,
        }),
    };

    getShippingCostList(params)
      .unwrap()
      .then(res => {
        if (!res?.shippingCosts?.length) {
          toast.error({
            title: intl.formatMessage({ id: 'Shipping cost not found' }),
          });
        } else {
          dispatch(setShippingCostList({ list: res }));
        }
      })
      .catch(err => {
        console.error('Error fetching shipping cost list:', err);
      })
      .finally(() => {
        setState(prev => ({ ...prev, isLoading: false }));
      });
  };

  const handleDeleteShippingCost = async item => {
    setState(prev => ({ ...prev, isRemoveModal: true }));
    try {
      await deleteShippingCost({ id: item.id }).unwrap();
      toast.success({
        title: intl.formatMessage({
          id: 'Shipping cost is deleted successfully',
        }),
      });
      handleApplyFilter();
    } catch (e) {
      toast.error({
        title: intl.formatMessage({
          id: 'Deleting shipping cost is failure',
        }),
      });
    }
  };

  const handleToggle = async e => {
    dispatch(
      setAvailable({
        available: e,
      }),
    );
    try {
      let payload = {
        isAvailable: e,
        whereInIds: list.shippingCosts.map(item => item.id),
      };
      await bulkIsAvailable(payload).unwrap();
      toast.success({
        title: intl.formatMessage({
          id: 'Shipping cost is updated successfully',
        }),
      });
      handleApplyFilter();
    } catch (e) {
      toast.error({
        title: intl.formatMessage({
          id: 'Updating shipping cost is failure',
        }),
      });
    }
  };

  return (
    <div className="shippingCost_list_layout">
      <Loading visible={state.isLoading} />
      <ContentHeader type="shippingcost_list" />
      <div className="shippingCost_filter_wrapper">
        <ShippingCostFilter
          onSearch={handleSearch}
          onKeyDownSearch={handleKeyDownSearch}
          onSearchDayPart={handleSearchDayPart}
          onApplyFilter={handleApplyFilter}
          onToggle={handleToggle}
          checkAvailable={available}
        />

        <ShippingCostTable
          ShippingCostData={list.shippingCosts || []}
          onChangePage={handleChangePage}
          onDeleteShippingCost={handleDeleteShippingCost}
          onIsAvailable={handleIsAvailable}
          total={list.count || 0}
        />
      </div>
    </div>
  );
};

export default ShippingCostList;
